import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { ReactFlowCanvasEdge } from '../../hooks/useYCanvas'

interface CanvasEdgesContextValue {
  edges: ReactFlowCanvasEdge[]
  renderActiveEdgesOnly: boolean
  hideConnectionHandles: boolean
}

const CanvasEdgesContext = createContext<CanvasEdgesContextValue | null>(null)
const EMPTY_CANVAS_EDGES: ReactFlowCanvasEdge[] = []

export function CanvasEdgesProvider({
  edges,
  renderActiveEdgesOnly = false,
  hideConnectionHandles = false,
  children,
}: {
  edges: ReactFlowCanvasEdge[]
  renderActiveEdgesOnly?: boolean
  hideConnectionHandles?: boolean
  children: ReactNode
}) {
  const value = useMemo(
    () => ({ edges, renderActiveEdgesOnly, hideConnectionHandles }),
    [edges, hideConnectionHandles, renderActiveEdgesOnly],
  )
  return (
    <CanvasEdgesContext.Provider value={value}>
      {children}
    </CanvasEdgesContext.Provider>
  )
}

export function useCanvasEdges(): ReactFlowCanvasEdge[] {
  const context = useContext(CanvasEdgesContext)
  return context?.edges ?? EMPTY_CANVAS_EDGES
}

export function useRenderActiveEdgesOnly(): boolean {
  return useContext(CanvasEdgesContext)?.renderActiveEdgesOnly ?? false
}

export function useHideConnectionHandles(): boolean {
  return useContext(CanvasEdgesContext)?.hideConnectionHandles ?? false
}
